<template>
  <div role="radiogroup">
    <v-radio-group hide-details  v-model="selectedAnswer" mandatory>
      <p aria-live="polite" class="aria-feedback">{{interactionFeedbackAria}}</p>
      <div
        class="radio-option"
        v-for="io in interaction.interactionOptions"
        :key="io.id"
        :class="[
        (
          interactionMeta.opacity === 1
          && !interactionMeta.darkMode
        )
          ? feedbackType(io)
          : 'transparent-mode-' + feedbackType(io),
        ]">
        <v-radio
          role="radio"
          :aria-labelledby="'io'+io.id+'label'"
          @change="interactionMeta.isAnswerEmpty = false"
          color="primary"
          :disabled="interactionMeta.isInteractionAnswered || interactionMeta.isProcessingData || (isLiveSession && interactionMeta.pastSessionTime)"
          hide-details
          :value="io"
          :dark="interactionMeta.darkMode"
          >
          <div slot="label" class="pp-interaction-body full-width" v-html="io.option" :id="'io'+io.id+'label'"></div>
        </v-radio>
        <interaction-attachments :attachments="io.interactionOptionAttachments" />
        <p
          v-if="isBroadcast && (user && user.userType === 'teacher')"
          class="broadcast__counter-tag primary--text">
            ({{ optionCount && optionCount[io.id] || 0 }})
        </p>
        <blockquote
          v-show="markAnswer(io)"
          :key="interactionRadioFeedbackKey"
          class="feedback-blockquote clearfix pp-interaction-feedback">
          <p class="mb-0" :class="{ 'white--text': interactionMeta.darkMode }">
            <span>{{formatFeedback(feedbackType(io))}}</span>
          </p>
          <div
            v-show="showFeedback(io)"
            v-html="io.feedback"
            :class="{ 'white--text': interactionMeta.darkMode }"
            tabindex="-1">
          </div>
          <interaction-attachments v-show="showFeedback(io)" :attachments="io.interactionOptionFeedbackAttachments" />
        </blockquote>
      </div>
    </v-radio-group>
  </div>
</template>

<script>
/* global _ */

// Components:
import EventBus from '@/buses/EventBus';
import InteractionAttachments from '@/components/interaction/InteractionAttachments';

// Libraries / Plugins
import {
  mapGetters,
  mapState,
} from 'vuex';

export default {
  name: 'interaction-radio',
  props: [
    'interactionId',
    'visible',
    'optionCount',
  ],
  components: {
    InteractionAttachments,
  },
  data() {
    return {
      interactionFeedbackAria: '',
      selectedAnswer: null,
      interactionRadioFeedbackKey: 1,
    };
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
    ]),
    ...mapGetters('player', [
      'user',
      'isBroadcast',
    ]),
    correctOptionIds() {
      return _.map(this.interactionMeta.correctAnswers, 'id');
    },
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
  },
  methods: {
    constructAriaFeedback() {
      let ariaFeedback = '';
      for (const io of this.interaction.interactionOptions) {
        let selectionFeedback = '';
        if (this.formatFeedback(this.feedbackType(io)) !== '') {
          selectionFeedback = `Answer ${io.option.replace(/(\<.*?\>)/g, '')} was ${this.formatFeedback(this.feedbackType(io))}. `;
          selectionFeedback += this.interactionAttempt.interactionOptionIds[0] === io.id ? `${this.showFeedback ? 'Feedback provided: ' + io.feedback.replace(/(\<.*?\>)/g, '') : ''} `: '';
        }
        ariaFeedback += selectionFeedback;
      }
      setTimeout(() => {this.interactionFeedbackAria = ariaFeedback;}, 100); // JAWS will not read aria-live region without this timeout
    },
    feedbackType(io) {
      if (this.markAnswer(io)) {
        // interaction answered
        // if hide_correct_answer_always - dont show correct/incorrect/missed
        if (
          (
            _.get(this, 'interaction.config.hide_correct_answer_always', false)
            && !_.get(this, 'interaction.config.only_show_correct_answer_on_last_attempt', false)
          )
          || (this.interactionMeta.attempts < this.interaction.attempts
          && _.get(this.interaction, 'config.show_highlighting_last_attempt_only', false)
          && !this.interactionMeta.isAnswerCorrect)
        ) return '';
        if (this.interactionAttempt.interactionOptionIds.includes(io.id)) {
          // option selected
          if (this.interaction.interactionType === 'poll_q') return 'selected'; // polling, use blue selected class
          return (this.interactionMeta.isAnswerCorrect) ? 'correct' : 'incorrect'; // mc, mark correct or incorrect
        }
        // correct answer was not selected
        return (
          this.correctOptionIds.includes(io.id)
          && (
            !_.get(this, 'interaction.config.hide_correct_answer_if_not_selected', false)
            || (
              _.get(this, 'interaction.config.only_show_correct_answer_on_last_attempt', false)
              && this.interactionMeta.attempts === this.interaction.attempts
            )
          )
        ) ? 'correct' : '';
      }
      return '';
    },
    interactionLabelAndFeedback(io) {
      return this.interactionAttempt.interactionOptionIds[0] === io.id ?
      `Answer ${io.option.replace(/(\<.*?\>)/g, '')} was ${this.formatFeedback(this.feedbackType(io))}. ${this.showFeedback ? io.feedback.replace(/(\<.*?\>)/g, '') : ''}` : '';
    },
    markAnswer(io) {
      return (
        this.interactionMeta.isInteractionAnswered
        && (
          this.interaction.attempts > 0
          && (this.interactionMeta.attempts >= this.interaction.attempts)
          || this.interactionMeta.isAnswerCorrect
          || !_.get(this.interaction, 'config.show_feedback_last_attempt_only', 1)
          || (
            this.interactionMeta.attempts === this.interaction.attempts
            && _.get(this.interaction, 'config.only_show_correct_answer_on_last_attempt', 0)
          )
        )
        && (
          this.interactionAttempt.interactionOptionIds.includes(io.id)
          || this.correctOptionIds.includes(io.id)
          || this.interaction.config.show_unselected_correct_feedback
        )
      );
    },
    showFeedback(io) {
      return (
        this.markAnswer(io)
        && (
          this.interaction.config.show_unselected_correct_feedback
          || this.interactionAttempt.interactionOptionIds.includes(io.id)
        )
        && (
          io.feedback
          || io.interactionOptionFeedbackAttachments.length > 0
        )
      );
    },
    formatFeedback(text) {
      if (text === 'selected') return '';
      return _.startCase(_.toLower(text));
    },
    handleAnsweredInteraction() {
      this.selectedAnswer = this.interaction.interactionOptions.find(io => io.id === this.interactionAttempt.interactionOptionIds[0]);
    },
  },
  watch: {
    'interactionMeta.isInteractionAnswered'() {
      this.constructAriaFeedback();
    },
    visible(newValue, oldValue) {
      if (!newValue && oldValue) this.interactionRadioFeedbackKey += 1;
    },
    selectedAnswer(interactionOption) {
      if (!interactionOption) return;
      this.interactionAttempt.interactionOptionIds = [interactionOption.id];
      this.interactionAttempt.responseText = [interactionOption.option];
    }
  },
  mounted() {
    EventBus.$on('retakeInteraction', interactionId => {
      if (interactionId === this.interactionId) {
        this.selectedAnswer = null;
        this.interactionRadioFeedbackKey += 1;
      }
    });
    this.$nextTick(() => {
      if (this.interactionMeta.isInteractionAnswered) this.handleAnsweredInteraction();
    });
  }
};
</script>

<style>
  .radio-option {
    position: relative;
  }
  .radio-option .v-radio .v-input--selection-controls__input {
    align-self: flex-start;
  }
  .radio-option audio, .checkbox-option {
    min-width: 220px;
  }
  .radio-option label {
    width: 83%;
  }
  .broadcast__counter-tag {
    position: absolute;
    right: 0;
    top: 0;
    padding: 5px 10px;
    line-height: 0.9em;
    font-size: 0.9em;
  }
</style>
